import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from './useAuth';
import { CircularProgress, Box } from '@mui/material';
import AccessDenied from '../pages/AccessDenied';

export type Role = 'guest' | 'participant' | 'coordinator' | 'creator' | 'org_admin';

const ROLE_HIERARCHY: Role[] = ['guest', 'participant', 'coordinator', 'creator', 'org_admin'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  minRole?: Role;
}

function hasMinRole(userRole: Role, min: Role): boolean {
  return ROLE_HIERARCHY.indexOf(userRole) >= ROLE_HIERARCHY.indexOf(min);
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, minRole }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh"
        }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (minRole && user && !hasMinRole(user.role, minRole)) {
    return <AccessDenied />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
